'use client';

import { FormEvent, useState } from "react";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { apiFetch, ApiError, apiRoutes } from "@/lib/api";
import { useUser } from "@/context/UserContext";

type LoginResponse = {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
};

export function LoginForm() {
  const { setToken } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    setError(null);

    try {
      // returnTo permite que, apos login SSO via Crie Oferta, o usuario seja
      // redirecionado de volta para a URL de origem (ex.: /arte/<id>).
      const currentReturnTo = typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("returnTo") || window.location.origin + "/")
        : "/";

      const response = await apiFetch<LoginResponse>(apiRoutes.auth.login(), {
        method: "POST",
        body: JSON.stringify({ email, password, returnTo: currentReturnTo })
      });

      setToken(response.token);
      
      // Use window.location.href instead of router.push to ensure:
      // 1. The cookie is properly committed before navigation
      // 2. Full page reload ensures middleware sees the new cookie
      // 3. Avoids race conditions with Next.js client-side navigation
      // Small timeout ensures cookie is written before navigation
      await new Promise(resolve => setTimeout(resolve, 50));
      window.location.href = "/";
    } catch (caughtError) {
      // Contas vinculadas ao Crie Oferta sao redirecionadas para o SSO.
      if (caughtError instanceof ApiError && caughtError.status === 403) {
        const body = caughtError.body as { requiresCrieOfertaSso?: boolean; ssoStartUrl?: string } | null;
        if (body?.requiresCrieOfertaSso && body.ssoStartUrl) {
          window.location.href = body.ssoStartUrl;
          return;
        }
      }
      const message = caughtError instanceof Error ? caughtError.message : "Não foi possível efetuar login.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Campo Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-xs font-medium text-white/70">
          Email
        </label>
        <div className="relative">
          <FontAwesomeIcon icon={faEnvelope} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-4 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
            placeholder="seu@email.com"
          />
        </div>
      </div>

      {/* Campo Senha */}
      <div className="space-y-1.5">
        <label htmlFor="password" className="text-xs font-medium text-white/70">
          Senha
        </label>
        <div className="relative">
          <FontAwesomeIcon icon={faLock} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white/70"
            tabIndex={-1}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Lembrar + Esqueci Senha */}
      <div className="flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-white/20 bg-white/10 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
          />
          <span className="text-xs text-white/60">Lembrar de mim</span>
        </label>
        <Link 
          href="/recuperar-senha" 
          className="text-xs font-medium text-brand-primary transition hover:text-brand-primary/80"
        >
          Esqueceu a senha?
        </Link>
      </div>

      {/* Erro */}
      {error ? (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
          {error}
        </p>
      ) : null}

      {/* Botão Entrar */}
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-full bg-brand-gradient py-3 text-sm font-bold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Entrando...
          </span>
        ) : (
          "Entrar"
        )}
      </button>

      {/* Termos */}
      <p className="text-center text-[11px] text-white/40">
        Ao continuar, você concorda com os{" "}
        <Link href="/termos" className="text-white/60 underline hover:text-white">
          Termos
        </Link>{" "}
        e{" "}
        <Link href="/privacidade" className="text-white/60 underline hover:text-white">
          Privacidade
        </Link>
      </p>
    </form>
  );
}
